'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Logger } from '@/core/utils/logger';
const logger = new Logger('components');
import ReactDOM from 'react-dom';
import { X, Check, RotateCw, ZoomIn, ZoomOut, Move } from 'lucide-react';
import { CropArea } from '@/core/utils/imageOptimization';

interface ImageCropperProps {
  imageUrl: string;
  aspectRatio?: number; // 1 for square/circle
  onCrop: (cropArea: CropArea) => void;
  onCancel: () => void;
  circular?: boolean;
}

export const ImageCropper: React.FC<ImageCropperProps> = ({
  imageUrl,
  aspectRatio: _aspectRatio = 1,
  onCrop,
  onCancel,
  circular = true
}) => {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = new Image();
    let isMounted = true;

    // Timeout to prevent hanging on slow/broken images
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        logger.warn('[ImageCropper] Image load timeout');
        // Set a default size so cropper still works
        setImageSize({ width: 400, height: 400 });
      }
    }, 10000);

    img.onload = () => {
      clearTimeout(timeoutId);
      if (isMounted) {
        setImageSize({ width: img.width, height: img.height });
      }
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      if (isMounted) {
        logger.error('[ImageCropper] Failed to load image');
        // Set a default size so cropper still works
        setImageSize({ width: 400, height: 400 });
      }
    };

    img.src = imageUrl;

    // Cleanup to prevent memory leak and state updates on unmounted component
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
      img.src = ''; // Cancel any pending load
    };
  }, [imageUrl]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    const newZoom = Math.min(Math.max(0.5, zoom + delta), 3);
    setZoom(newZoom);
  }, [zoom]);

  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 0.1, 0.5));
  };

  const handleReset = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleCrop = () => {
    if (!containerRef.current || !imageRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    // Use 70% of container size for the crop circle (matching the visual circle)
    const cropSize = Math.min(container.width, container.height) * 0.7;
    const cropX = (container.width - cropSize) / 2;
    const cropY = (container.height - cropSize) / 2;

    // Calculate the crop area in original image coordinates
    const scaleX = imageSize.width / (container.width * zoom);
    const scaleY = imageSize.height / (container.height * zoom);

    const cropArea: CropArea = {
      x: Math.max(0, (cropX - position.x) * scaleX),
      y: Math.max(0, (cropY - position.y) * scaleY),
      width: cropSize * scaleX,
      height: cropSize * scaleY
    };

    // Ensure crop area is within image bounds
    cropArea.width = Math.min(cropArea.width, imageSize.width - cropArea.x);
    cropArea.height = Math.min(cropArea.height, imageSize.height - cropArea.y);

    onCrop(cropArea);
  };

  // Portal the modal to body to ensure proper z-index stacking
  const modalContent = (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 1300 }}>
      {/* Semi-transparent backdrop */}
      <div
        className="absolute inset-0 bg-black opacity-50 transition-opacity backdrop-blur-sm duration-300"
        onClick={onCancel}
      />
      
      <div className="relative w-full max-w-2xl mx-4 z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 text-white">
          <h3 className="text-lg font-semibold">Adjust Profile Image</h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cropper Container - Square aspect ratio for circular crop */}
        <div className="flex items-center justify-center">
          <div
            ref={containerRef}
            className="relative bg-gray-900 rounded-lg overflow-hidden cursor-move"
            style={{
              width: '400px',
              height: '400px',
              maxWidth: '90vw',
              maxHeight: '60vh',
              aspectRatio: '1 / 1'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            {/* Image */}
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Crop preview"
              className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                transition: isDragging ? 'none' : 'transform 0.1s'
              }}
              draggable={false}
            />

            {/* Crop Overlay */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Semi-transparent overlay with cutout */}
              <svg className="absolute inset-0 w-full h-full">
                <defs>
                  <mask id="crop-mask">
                    <rect x="0" y="0" width="100%" height="100%" fill="white" />
                    {circular ? (
                      <circle
                        cx="50%"
                        cy="50%"
                        r="35%"
                        fill="black"
                      />
                    ) : (
                      <rect
                        x="10%"
                        y="10%"
                        width="80%"
                        height="80%"
                        fill="black"
                      />
                    )}
                  </mask>
                </defs>
                <rect
                  x="0"
                  y="0"
                  width="100%"
                  height="100%"
                  fill="rgba(0, 0, 0, 0.5)"
                  mask="url(#crop-mask)"
                />
              </svg>
              
              {/* Crop border - perfect circle */}
              <div
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-2 border-white pointer-events-none"
                style={{
                  width: '70%',
                  height: '70%',
                  borderRadius: circular ? '50%' : '0',
                  minWidth: '200px',
                  minHeight: '200px',
                  maxWidth: '280px',
                  maxHeight: '280px'
                }}
              />
            </div>

            {/* Drag Indicator */}
            {isDragging && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/75 text-white px-3 py-1 rounded-full text-sm">
                <Move className="inline w-4 h-4 mr-1" />
                Drag to position
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomOut}
              className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <div className="px-3 py-1 bg-gray-800 text-white rounded-lg min-w-[60px] text-center">
              {Math.round(zoom * 100)}%
            </div>
            <button
              onClick={handleZoomIn}
              className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            <button
              onClick={handleReset}
              className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors ml-2"
              title="Reset"
            >
              <RotateCw className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCrop}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Check className="w-5 h-5" />
              Apply
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-4 text-center text-gray-400 text-sm">
          Drag to move | Scroll to zoom | Position your face in the circle
        </div>
      </div>
    </div>
  );

  // Use portal to render at body level
  if (typeof document !== 'undefined') {
    return ReactDOM.createPortal(modalContent, document.body);
  }
  
  return modalContent;
};